import { MC_URL } from "./config";

// Sign-in goes through Mission Control: exchange a Google ID token for an
// MC-issued access token, which is then used for all ledger API calls.
//
// That access token is short-lived (1h) and lives in MEMORY ONLY — never
// localStorage. What keeps a tester signed in is the rotating refresh token MC
// plants in an httpOnly cookie at sign-in: invisible to JS, good for 30 days of
// idle, and exchanged by refreshSession() for a fresh access token whenever the
// current one expires. Every mesh SPA does this the same way — MC is the
// platform's auth provider, so each client renews silently instead of bouncing
// the user back to Google every hour. Reference implementation:
// qa-mission-control/web/src/lib/auth.ts.
const USER_KEY = "mc_user";
const LOGOUT_EVENT = "auth:logout";

// Superseded by the in-memory token below. Cleared on load so a token persisted
// by an older build doesn't sit in localStorage indefinitely. Guarded because
// this runs at import: a storage-less context must not fail to load the module.
try {
  localStorage.removeItem("mc_access_token");
} catch {
  /* ignore */
}

let accessToken: string | null = null;

// Idempotence guard: several parallel requests can 401 at once, but only the
// first teardown should notify.
let sessionEnded = false;

export type AuthUser = {
  email?: string | null;
  user_id?: string | null;
  name?: string | null;
};

export function getToken(): string | null {
  return accessToken;
}

/** The last-known user (display info only). Persisted so the shell can paint
 *  immediately on reload while the silent refresh confirms the session. */
export function getUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    localStorage.removeItem(USER_KEY);
    return null;
  }
}

function setSession(token: string, user?: AuthUser) {
  accessToken = token;
  sessionEnded = false;
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/** Exchange a Google ID token for a Mission Control session.
 *  `credentials: "include"` is what lets MC plant the httpOnly refresh cookie —
 *  without it the browser drops the Set-Cookie and the session dies at the
 *  access token's 1-hour expiry with no way to renew. */
export async function loginWithGoogle(idToken: string): Promise<AuthUser> {
  const res = await fetch(`${MC_URL}/auth/google`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id_token: idToken }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? "Sign-in failed");
  }
  const data = (await res.json()) as { access_token: string; user: AuthUser };
  setSession(data.access_token, data.user);
  return data.user;
}

// Single-flight guard: a burst of 401s (or several tabs) must not fire several
// concurrent /auth/refresh calls — rotation would invalidate all but one. In a
// tab, `inflight` dedupes; across tabs, the Web Locks API serializes so only one
// refresh touches the (browser-global) cookie at a time.
let inflight: Promise<boolean> | null = null;

/** Renew the access token from the httpOnly refresh cookie. Resolves true when
 *  a session is live afterwards, false when the user must sign in again. */
export function refreshSession(): Promise<boolean> {
  if (inflight) return inflight;
  inflight = withLock("mc-session-refresh", doRefresh).finally(() => {
    inflight = null;
  });
  return inflight;
}

async function withLock(name: string, fn: () => Promise<boolean>): Promise<boolean> {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (locks && typeof locks.request === "function") {
    // Awaited, not cast: LockManager.request is typed as resolving to the
    // callback's own return value, so casting the nested promise doesn't
    // typecheck — awaiting flattens it.
    return await locks.request(name, fn);
  }
  return fn();
}

// Web Locks are per-ORIGIN, so the lock above does not serialize this app against
// the mesh's other SPAs — and they all share one browser-global refresh cookie.
// Losing a rotation race is therefore normal, not exceptional: the winner has
// already planted a fresh cookie, so retrying once — after a beat, to let its
// Set-Cookie land — succeeds. MC's /auth/refresh leaves the cookie in place on a
// failed rotation precisely so this retry is possible.
const REFRESH_RETRY_DELAY_MS = 250;

async function doRefresh(): Promise<boolean> {
  if (await attemptRefresh()) return true;
  await new Promise((resolve) => setTimeout(resolve, REFRESH_RETRY_DELAY_MS));
  return attemptRefresh();
}

async function attemptRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${MC_URL}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) {
      accessToken = null;
      return false;
    }
    const data = (await res.json()) as { access_token: string; user: AuthUser };
    setSession(data.access_token, data.user);
    return true;
  } catch {
    return false;
  }
}

/** Local teardown: drop the in-memory token and show the login screen. For a
 *  session that is already gone (a 401 that survived a silent refresh).
 *  Deliberately does NOT call /auth/logout: the refresh cookie is shared with
 *  the rest of the mesh, and revoking it on a transient failure would sign the
 *  user out of Mission Control and every sibling app too. */
export function endSession({ reason = null }: { reason?: string | null } = {}) {
  if (sessionEnded) return;
  sessionEnded = true;
  accessToken = null;
  localStorage.removeItem(USER_KEY);
  window.dispatchEvent(new CustomEvent(LOGOUT_EVENT, { detail: { reason } }));
}

/** Explicit sign-out: revoke the refresh token server-side, then tear down.
 *  This ends the shared MC session — signing out here signs the user out of the
 *  other mesh apps as well, which is the point of a single sign-out. */
export async function signOut(): Promise<void> {
  try {
    await fetch(`${MC_URL}/auth/logout`, { method: "POST", credentials: "include" });
  } catch {
    // best-effort; tear down locally regardless
  }
  endSession({ reason: "signed-out" });
}

/** Notifies on session teardown in this tab (custom event) or another tab
 *  (storage event when the persisted user is removed elsewhere). */
export function subscribeToLogout(cb: (reason: string | null) => void): () => void {
  const onEvent = (e: Event) => cb((e as CustomEvent<{ reason?: string | null }>).detail?.reason ?? null);
  const onStorage = (e: StorageEvent) => {
    if (e.key === USER_KEY && e.newValue == null) {
      sessionEnded = true;
      accessToken = null;
      cb("signed-out-elsewhere");
    }
  };
  window.addEventListener(LOGOUT_EVENT, onEvent);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(LOGOUT_EVENT, onEvent);
    window.removeEventListener("storage", onStorage);
  };
}
